// src/components/exam-builder/WritingPromptModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { FolderOpen, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import MediaPickerModal from "./MediaPickerModal";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  // صورت سؤال
  stem_fa: "",
  stem_en: "",

  // متن Reading (اختیاری)
  text_fa: "",
  text_en: "",

  // فایل صوتی (اختیاری)
  audio_url: "",

  minWords: 0,
  maxWords: 0,
  timeLimitSec: 0,
  resources: [""],
  points: 10,
  status: "published",
  layout: "top-bottom",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// تبدیل آیتم از دیتابیس → فرم
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem = item.stem || {};
  const body = item.body || {};

  // اسکیمای جدید:
  const text = body.text || {};
  const audio_url =
    body.audio_url ||
    (body.audio && (body.audio.url || body.audio.audio_url)) ||
    "";

  // اسکیمای قدیمی (برای سازگاری عقب‌گرد):
  const oldPromptType = body.prompt_type;
  const oldPromptContent = body.prompt_content || {};

  const stem_fa = stem.fa || "";
  const stem_en = stem.en || "";

  const text_fa =
    text.fa || (oldPromptType === "reading" ? oldPromptContent.fa || "" : "");
  const text_en =
    text.en || (oldPromptType === "reading" ? oldPromptContent.en || "" : "");

  const finalAudioUrl =
    audio_url ||
    (oldPromptType === "listening" ? oldPromptContent.audio_url || "" : "");

  const resources = Array.isArray(body.resources) ? body.resources : [];

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    text_fa,
    text_en,
    audio_url: finalAudioUrl,
    minWords:
      typeof body.minWords === "number" && !Number.isNaN(body.minWords)
        ? body.minWords
        : 0,
    maxWords:
      typeof body.maxWords === "number" && !Number.isNaN(body.maxWords)
        ? body.maxWords
        : 0,
    timeLimitSec:
      typeof body.timeLimitSec === "number" && !Number.isNaN(body.timeLimitSec)
        ? body.timeLimitSec
        : 0,
    resources: resources.length ? resources : [""],
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 10,
    status: item.status || "published",
    layout: body.layout || "top-bottom",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : item.tags || "",
    explanation_fa,
    explanation_en,
  };
};

export default function WritingPromptModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null, // آیتم برای ادیت
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());
  const [fileModalOpen, setFileModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addResource = () =>
    setForm((f) => ({ ...f, resources: [...f.resources, ""] }));

  const removeResource = (idx) =>
    setForm((f) => ({
      ...f,
      resources: f.resources.filter((_, i) => i !== idx),
    }));

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();

    // ✅ فقط صورت سؤال الزامیه (حداقل یکی از fa/en)
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("writing_prompt.toasts.prompt_required"));
      return;
    }

    if (
      form.minWords &&
      form.maxWords &&
      Number(form.minWords) > Number(form.maxWords)
    ) {
      toast.error(t("writing_prompt.toasts.min_gt_max"));
      return;
    }

    const payload = {
      kind: "writing_prompt",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        // متن reading اختیاری
        text: {
          fa: form.text_fa || undefined,
          en: form.text_en || undefined,
        },
        // فایل صوتی اختیاری
        audio_url: form.audio_url || undefined,
        minWords: Number(form.minWords) || 0,
        maxWords: Number(form.maxWords) || 0,
        timeLimitSec: Number(form.timeLimitSec) || undefined,
        resources: (form.resources || []).map((r) => r.trim()).filter(Boolean),
        layout: form.layout,
      },
      points: Number(form.points) || 0,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("writing_prompt.toasts.updated", "ویرایش شد")
          : t("writing_prompt.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* صورت سؤال (stem) */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t("writing_prompt.stem_label", "صورت سؤال")}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-xs font-medium mb-1">
              {t("writing_prompt.stem_fa", "صورت سؤال (فارسی)")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                handleEditorChange("stem_fa", content)
              }
              placeholder={t(
                "writing_prompt.stem_fa_ph",
                "صورت سؤال را به فارسی بنویسید"
              )}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-xs font-medium mb-1">
              {t("writing_prompt.stem_en", "Question prompt (English)")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                handleEditorChange("stem_en", content)
              }
              placeholder={t(
                "writing_prompt.stem_en_ph",
                "Write the question prompt in English"
              )}
            />
          </div>
        </div>
      </div>

      {/* متن Reading (اختیاری) */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t("writing_prompt.reading_text_label", "متن Reading (اختیاری)")}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-xs font-medium mb-1">
              {t("writing_prompt.reading_text_fa", "متن (فارسی)")}
            </label>
            <CustomEditor
              value={form.text_fa}
              onEditorChange={(content) =>
                handleEditorChange("text_fa", content)
              }
              placeholder={t(
                "writing_prompt.reading_text_fa_ph",
                "در صورت نیاز متن فارسی را وارد کنید"
              )}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-xs font-medium mb-1">
              {t("writing_prompt.reading_text_en", "Reading text (English)")}
            </label>
            <CustomEditor
              value={form.text_en}
              onEditorChange={(content) =>
                handleEditorChange("text_en", content)
              }
              placeholder={t(
                "writing_prompt.reading_text_en_ph",
                "Optional English reading passage"
              )}
            />
          </div>
        </div>
      </div>

      {/* فایل صوتی (اختیاری) */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("writing_prompt.audio_url_label", "آدرس فایل صوتی (اختیاری)")}
        </label>
        <div className="flex gap-2">
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t(
              "writing_prompt.audio_url_ph",
              "مثال: https://.../audio.mp3"
            )}
            value={form.audio_url}
            onChange={(e) =>
              setForm((f) => ({ ...f, audio_url: e.target.value }))
            }
          />
          <button
            type="button"
            onClick={() => setFileModalOpen(true)}
            className="px-3 py-2 rounded-lg border bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 whitespace-nowrap"
            title={t("common.browse", "Browse")}
          >
            <FolderOpen className="w-4 h-4" />
            {t("common.browse", "Browse")}
          </button>
        </div>
      </div>

      {/* Layout Selector (مثل قبل) */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("select_sentence.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`select_sentence.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Constraints */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("writing_prompt.min_words")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("writing_prompt.min_words_ph")}
            value={form.minWords}
            onChange={(e) =>
              setForm((f) => ({ ...f, minWords: e.target.value }))
            }
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("writing_prompt.max_words")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("writing_prompt.max_words_ph")}
            value={form.maxWords}
            onChange={(e) =>
              setForm((f) => ({ ...f, maxWords: e.target.value }))
            }
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("writing_prompt.time_limit")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("writing_prompt.time_limit_ph")}
            value={form.timeLimitSec}
            onChange={(e) =>
              setForm((f) => ({ ...f, timeLimitSec: e.target.value }))
            }
          />
        </div>
      </div>

      {/* Resources */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("writing_prompt.resources")}
          </label>
          <button
            type="button"
            className="text-blue-600 text-sm"
            onClick={addResource}
          >
            {t("writing_prompt.add_resource")}
          </button>
        </div>
        <div className="space-y-2">
          {form.resources.map((r, idx) => (
            <div key={idx} className="flex gap-2">
              <input
                className="border rounded-lg px-3 py-2 flex-1"
                placeholder={t("writing_prompt.resource_url_ph")}
                value={r}
                onChange={(e) => {
                  const resources = [...form.resources];
                  resources[idx] = e.target.value;
                  setForm((f) => ({ ...f, resources }));
                }}
              />
              {form.resources.length > 1 && (
                <button
                  type="button"
                  className="px-2 rounded border text-red-600"
                  onClick={() => removeResource(idx)}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("writing_prompt.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("writing_prompt.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("writing_prompt.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>

      <MediaPickerModal
        isOpen={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        token={token}
      />
    </form>
  );
}
